import React, { useEffect, useRef, useState } from "react";
import { ClockStyle } from "../../types/LayoutTypes";

interface AnalogClockProps {
  size: number;
  isDarkMode?: boolean;
  showSeconds?: boolean;
  fixedTime?: string; // Optional fixed time in HH:MM format
  hourHandColor?: string;
  minuteHandColor?: string;
  secondHandColor?: string;
  hourHandThickness?: number;
  minuteHandThickness?: number;
  hourHandLength?: number;
  minuteHandLength?: number;
  centerPointSize?: number;
  centerPointColor?: string;
  showFace?: boolean;
  showBorder?: boolean;
  showMarkings?: boolean;
  showCenterPoint?: boolean;
  minuteMarkerSize?: number;
  minuteMarkerThickness?: number;
  minuteMarkerColor?: string;
  showMinuteMarkers?: boolean;
  show5MinuteMarkers?: boolean;
  fiveMinuteMarkerSize?: number;
  fiveMinuteMarkerThickness?: number;
  fiveMinuteMarkerColor?: string;
  clockBackgroundColor?: string;
  className?: string;
}

const AnalogClock: React.FC<AnalogClockProps> = ({
  size,
  isDarkMode = false,
  showSeconds = true,
  fixedTime,
  hourHandColor = "#000000",
  minuteHandColor = "#000000",
  secondHandColor = "#ff0000",
  hourHandThickness = 6,
  minuteHandThickness = 4,
  hourHandLength,
  minuteHandLength,
  centerPointSize = 5,
  centerPointColor,
  showFace = true,
  showBorder = true,
  showMarkings = true,
  showCenterPoint = true,
  minuteMarkerSize,
  minuteMarkerThickness,
  minuteMarkerColor,
  showMinuteMarkers = true,
  show5MinuteMarkers = true,
  fiveMinuteMarkerSize,
  fiveMinuteMarkerThickness,
  fiveMinuteMarkerColor,
  clockBackgroundColor,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [time, setTime] = useState(new Date());

  // Update time every second if not using fixed time
  useEffect(() => {
    if (fixedTime) return;

    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [fixedTime]);

  // Parse fixed time if provided
  useEffect(() => {
    if (fixedTime) {
      const [hours, minutes] = fixedTime.split(":").map(Number);
      const fixedDate = new Date();
      fixedDate.setHours(hours);
      fixedDate.setMinutes(minutes);
      fixedDate.setSeconds(0);
      setTime(fixedDate);
    }
  }, [fixedTime]);

  // Draw clock
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Set dimensions
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 5;

    // Draw clock face
    if (showFace) {
      ctx.fillStyle =
        clockBackgroundColor || (isDarkMode ? "#333333" : "#f0f0f0");
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.fill();
    }

    // Draw border
    if (showBorder) {
      ctx.strokeStyle = isDarkMode ? "#555555" : "#000000";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Draw 5-minute markings (12 ticks at each 5-min position)
    if (showMarkings && show5MinuteMarkers) {
      ctx.strokeStyle =
        fiveMinuteMarkerColor || (isDarkMode ? "#ffffff" : "#333333");
      ctx.lineWidth = fiveMinuteMarkerThickness || 2;
      const fiveMinLen = fiveMinuteMarkerSize ?? 15;
      for (let i = 0; i < 12; i++) {
        const angle = (i * Math.PI) / 6;
        const startX = centerX + (radius - fiveMinLen) * Math.sin(angle);
        const startY = centerY - (radius - fiveMinLen) * Math.cos(angle);
        const endX = centerX + radius * Math.sin(angle);
        const endY = centerY - radius * Math.cos(angle);

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }
    }

    // Draw minute markings (48 short ticks at non-5-minute positions)
    if (showMarkings && showMinuteMarkers) {
      ctx.strokeStyle =
        minuteMarkerColor || (isDarkMode ? "#ffffff" : "#333333");
      ctx.lineWidth = minuteMarkerThickness || 1;
      for (let i = 0; i < 60; i++) {
        if (i % 5 !== 0) {
          // Skip positions where 5-minute marks are
          const angle = i * (Math.PI / 30); // 6 degrees per minute
          const markerLength = minuteMarkerSize || 3; // Length of the minute marker line
          const startX = centerX + (radius - markerLength) * Math.sin(angle);
          const startY = centerY - (radius - markerLength) * Math.cos(angle);
          const endX = centerX + radius * Math.sin(angle);
          const endY = centerY - radius * Math.cos(angle);

          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(endX, endY);
          ctx.stroke();
        }
      }
    }

    // Get time values
    const hours = time.getHours() % 12;
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();

    // Draw hour hand
    const hourAngle = ((hours + minutes / 60) * Math.PI) / 6;
    const hourHandLengthValue = hourHandLength || radius * 0.5;
    ctx.strokeStyle = hourHandColor;
    ctx.lineWidth = hourHandThickness;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(
      centerX + hourHandLengthValue * Math.sin(hourAngle),
      centerY - hourHandLengthValue * Math.cos(hourAngle),
    );
    ctx.stroke();

    // Draw minute hand
    const minuteAngle = ((minutes + seconds / 60) * Math.PI) / 30;
    const minuteHandLengthValue = minuteHandLength || radius * 0.7;
    ctx.strokeStyle = minuteHandColor;
    ctx.lineWidth = minuteHandThickness;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(
      centerX + minuteHandLengthValue * Math.sin(minuteAngle),
      centerY - minuteHandLengthValue * Math.cos(minuteAngle),
    );
    ctx.stroke();

    // Draw second hand if enabled
    if (showSeconds) {
      const secondAngle = (seconds * Math.PI) / 30;
      const secondHandLength = radius * 0.8;
      ctx.strokeStyle = secondHandColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + secondHandLength * Math.sin(secondAngle),
        centerY - secondHandLength * Math.cos(secondAngle),
      );
      ctx.stroke();
    }

    // Draw center point
    if (showCenterPoint) {
      ctx.fillStyle = centerPointColor || (isDarkMode ? "#ffffff" : "#000000");
      ctx.beginPath();
      ctx.arc(centerX, centerY, centerPointSize, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [
    size,
    time,
    isDarkMode,
    showSeconds,
    hourHandColor,
    minuteHandColor,
    secondHandColor,
    hourHandThickness,
    minuteHandThickness,
    hourHandLength,
    minuteHandLength,
    centerPointSize,
    centerPointColor,
    showFace,
    showBorder,
    showMarkings,
    showCenterPoint,
    minuteMarkerSize,
    minuteMarkerThickness,
    clockBackgroundColor,
  ]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className={`analog-clock ${className}`}
      style={{ display: "block" }}
    />
  );
};

export default AnalogClock;
